import { getTimeStamp } from '@mono/packages/utils/date-utils'
import { endOfDay, startOfDay } from 'date-fns'
import { FILTER_TYPES } from '../../utils/constants'
import { FILTER_OPERATORS } from './constants'
import {
  mapToNumberValue,
  mapToStringOrBoolOrNumberValue,
  mapToStringValue,
  mapToTimestampValue
} from './mappers'

export enum FilterOperator {
  Contains = 'CONTAINS',
  Equals = 'EQUALS',
  In = 'IN',
}

const getRangeFilter = <T = string, P = FilterOperator>(
  field: T,
  { from, to }
) => {
  return [
    {
      field,
      operator: FILTER_OPERATORS.greaterThanOrEqual as P,
      values: from
    },
    {
      field,
      operator: FILTER_OPERATORS.lessThanOrEqual as P,
      values: to
    }
  ]
}

export const setFilter = <T = string, P = FilterOperator>(
  field: T,
  filterDef: {
    values: any[]
  }
) => {
  return [
    {
      field,
      operator: FilterOperator.In as P,
      values: mapToStringOrBoolOrNumberValue(filterDef.values)
    }
  ]
}

export const textFilter = <T = string, P = FilterOperator>(
  field: T,
  filterDef: {
    filter: string
  },
  operator: P
) => {
  return [
    {
      field,
      operator,
      values: mapToStringValue([filterDef.filter])
    }
  ]
}

export const dateFilter = <T = string, P = FilterOperator>(
  field: T,
  filterDef: {
    dateFrom: string | Date
    dateTo?: string | Date
  },
  operator: P
) => {
  if (operator === FILTER_OPERATORS.equals) {
    const dateFromDate = new Date(filterDef.dateFrom)

    return [
      {
        field,
        operator: FilterOperator.Equals as P,
        values: mapToTimestampValue([
          getTimeStamp(startOfDay(dateFromDate), true)
        ])
      }
    ]
  }

  if (operator === FILTER_OPERATORS.inRange) {
    return calcualateDateRange<T, P>(
      field,
      filterDef.dateFrom,
      filterDef.dateTo
    )
  }

  return calculateGreaterOrLessThan<T, P>(field, operator, filterDef.dateFrom)
}

export const dateTimeFilter = <T = string, P = FilterOperator>(
  field: T,
  filterDef: {
    dateFrom: string | Date
    dateTo?: string | Date
  },
  operator: P
) => {
  if (operator === FILTER_OPERATORS.equals) {
    const dateFromDate = new Date(filterDef.dateFrom)

    return getRangeFilter<T, P>(field, {
      from: mapToTimestampValue([getTimeStamp(startOfDay(dateFromDate), true)]),
      to: mapToTimestampValue([getTimeStamp(endOfDay(dateFromDate), true)])
    })
  }

  if (operator === FILTER_OPERATORS.inRange) {
    return calcualateDateRange<T, P>(
      field as T,
      filterDef.dateFrom,
      filterDef.dateTo
    )
  }

  return calculateGreaterOrLessThan<T, P>(
    field as T,
    operator as P,
    filterDef.dateFrom
  )
}

const calcualateDateRange = <T = string, P = FilterOperator>(
  field: T,
  dateFrom: string | Date,
  dateTo: string | Date
) => {
  const dateFromDate = new Date(dateFrom)
  const dateToDate = new Date(dateTo)

  return getRangeFilter<T, P>(field, {
    from: mapToTimestampValue([getTimeStamp(startOfDay(dateFromDate), true)]),
    to: mapToTimestampValue([getTimeStamp(endOfDay(dateToDate), true)])
  })
}

const calculateGreaterOrLessThan = <T = string, P = FilterOperator>(
  field: T,
  operator: P,
  dateFrom: string | Date
) => {
  const dateFn =
    operator === FILTER_OPERATORS.greaterThanOrEqual ? startOfDay : endOfDay

  const timeStamp = getTimeStamp(dateFn(new Date(dateFrom)), true)

  return [
    {
      field,
      operator,
      values: mapToTimestampValue([timeStamp])
    }
  ]
}

export const numberFilter = (
  field: string,
  filterDef: {
    filter: number
    filterTo?: number
  },
  operator: string
) => {
  if (operator === FILTER_OPERATORS.inRange) {
    return getRangeFilter(field, {
      from: mapToNumberValue([filterDef.filter]),
      to: mapToNumberValue([filterDef.filterTo])
    })
  }

  return [
    {
      field,
      operator,
      values: mapToNumberValue([filterDef.filter])
    }
  ]
}

export const filterResolvers = {
  [FILTER_TYPES.DATE]: dateFilter,
  [FILTER_TYPES.DATE_TIME]: dateTimeFilter,
  [FILTER_TYPES.NUMBER]: numberFilter,
  [FILTER_TYPES.TEXT]: textFilter,
  [FILTER_TYPES.SET]: setFilter
}
